import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  ColorInput,
  ColorSwatch,
  Divider,
  Grid,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { isHexColor } from '../../helpers/brandColors';
import {
  WIDGET_ROLES,
  brandColorsState,
  contrastIssues,
  readableFix,
  resolveWidgetColors,
} from '../../helpers/widgetColors';
import { COLOR_ROLES, DARK_ROLES } from '../BrandContext/brandIdentity';
import HelpPopover from '../HelpPopover/HelpPopover';
import SettingRow from '../SettingRow/SettingRow';
import WidgetColorPreview, { SIDE_OPTIONS } from './WidgetColorPreview';

// The chat widget's colors: where they come from (Releva default, the approved Brand Identity, or
// the brand with the client's own changes), light / dark / the visitor's device, and a live
// preview. Stored in chatBot.appearance.colors; a row without it renders exactly as before.

const HELP = {
  source: {
    label: 'Colors',
    what: 'Where the chat takes its colors from.',
    examples: [
      { label: "Keep Releva's look → Releva default", value: 'default' },
      { label: 'Match my website → Brand Identity', value: 'brand' },
      { label: 'My brand, with a few colors changed → Custom', value: 'custom' },
    ],
    watchOut: 'Brand Identity stays in sync: when your brand colors change, the chat follows.',
  },
  mode: {
    label: 'Appearance',
    what: "Light or dark chat window. Match visitor's device follows the shopper's phone or computer setting.",
    examples: [
      { label: 'Light → most shops', value: 'light' },
      { label: "Match visitor's device → sites with a dark mode", value: 'auto' },
      { label: 'Dark → dark-themed stores', value: 'dark' },
    ],
    watchOut: "Check the Dark preview before choosing Match visitor's device.",
  },
};

const MODE_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'auto', label: "Match visitor's device" },
];

const BRAND_LABELS = Object.fromEntries([
  ...COLOR_ROLES.map(({ key, label }) => [`colors.${key}`, label]),
  ...DARK_ROLES.map(({ key, label }) => [`darkColors.${key}`, `dark ${label}`]),
]);

// Where a resolved value came from, in the client's words.
const sourceCaption = (from) => {
  if (from === 'override') return 'custom';
  if (from === 'legacy') return 'your main color';
  if (from === 'default') return 'Releva default';
  if (from.startsWith('derived.')) return 'worked out from your brand';
  return `brand · ${BRAND_LABELS[from] || from}`;
};

const NOTICES = {
  proposed: {
    text: 'Your brand colors are waiting for your OK. Once confirmed, the chat can use them.',
    action: 'Review and confirm',
    path: 'confirm/brandContext',
  },
  running: {
    text: "We're reading your website for your colors. This usually takes a minute or two. You can keep editing.",
  },
  missing: {
    text: 'No brand colors yet. Set up Brand Identity and the chat can match your site automatically.',
    action: 'Set up Brand Identity',
    path: 'brandContext',
  },
};

// Brand links open in a new tab, so unsaved sales agent changes are not lost.
const BrandLink = ({ href, children, ...props }) => (
  <Anchor href={href} target="_blank" rel="noopener noreferrer" size="sm" {...props}>
    {children} ↗
  </Anchor>
);

const ContrastLine = ({ issues }) =>
  issues.length ? (
    <Text size="xs" c="orange.8">
      ⚠ {issues.length} color pair{issues.length > 1 ? 's are' : ' is'} hard to read:{' '}
      {issues.map((issue) => `${issue.label} (${issue.side})`).join(', ')}. Saving still works.
    </Text>
  ) : (
    <Text size="xs" c="teal.8">
      ✓ All text is readable (WCAG AA).
    </Text>
  );

// One resolved role, read-only: swatch, name, hex and where it came from.
const SwatchRow = ({ role, palette, from, note = '', ...props }) => (
  <Group gap="xs" wrap="nowrap" {...props}>
    <ColorSwatch color={palette[role.key]} size={20} withShadow />
    <Box miw={0}>
      <Text size="sm" fw={500}>
        {role.label}
      </Text>
      <Text size="xs" c="dimmed">
        {palette[role.key].toUpperCase()} · {sourceCaption(from[role.key])}
        {note}
      </Text>
    </Box>
  </Group>
);

// A hex field that only reports a complete color, and only when it differs from the shown one,
// so opening and closing the picker stores nothing.
const HexInput = ({ value, onCommit, ...props }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <ColorInput
      format="hex"
      size="xs"
      value={draft}
      onChange={(next) => {
        setDraft(next);
        if (isHexColor(next) && next.toLowerCase() !== value.toLowerCase()) {
          onCommit(next.toLowerCase());
        }
      }}
      {...props}
    />
  );
};

const WidgetColorsSection = ({ chatBot, handleChange, setAppearance, brandContext, brandHref }) => {
  const resolved = resolveWidgetColors({
    colors: chatBot.appearance?.colors,
    customThemeColor: chatBot.customThemeColor,
    brandContext,
  });
  const { settings, ready } = resolved;
  const brandState = brandColorsState(brandContext);
  const [side, setSide] = useState(settings.mode === 'dark' ? 'dark' : 'light');
  const [activeRole, setActiveRole] = useState(null);
  const [askKeep, setAskKeep] = useState(null);

  const sides = settings.mode === 'light' ? ['light'] : ['light', 'dark'];
  const issues = sides.flatMap((name) => contrastIssues(resolved[name]).map((issue) => ({ ...issue, side: name })));
  const overrideCount = Object.keys(settings.light).length + Object.keys(settings.dark).length;
  const legacyColor =
    settings.source === 'default' && isHexColor(chatBot.customThemeColor) ? chatBot.customThemeColor : null;
  const editSide = settings.mode === 'light' ? 'light' : side;
  const brandIdentityHref = `${brandHref}/brandContext`;

  // The whole colors object is written each time, so a removed override is really gone.
  const setColors = (patch) => setAppearance({ colors: { ...settings, ...patch } });
  const setOverride = (name, key, value) => {
    const next = { ...settings[name] };
    if (value) next[key] = value;
    else delete next[key];
    setColors({ [name]: next });
  };
  const pickSource = (source) => {
    // The help popover's examples skip the control's disabled state.
    if (source === 'brand' && !ready) return;
    // A client who set their own main color is asked once whether to keep it.
    if (legacyColor && source !== 'default') {
      setAskKeep(source);
      return;
    }
    setAskKeep(null);
    setColors({ source });
  };
  const pickMode = (mode) => {
    setColors({ mode });
    setSide(mode === 'dark' ? 'dark' : 'light');
  };
  const roleHandlers = (key) => ({
    onMouseEnter: () => setActiveRole(key),
    onMouseLeave: () => setActiveRole(null),
    onFocus: () => setActiveRole(key),
    onBlur: () => setActiveRole(null),
  });

  // No notice while the brand is still loading (undefined), so "No brand colors yet" does not flash.
  const notice = brandContext !== undefined && NOTICES[brandState];
  const brandNotice = notice && (
    <Alert color="gray" variant="light" p="xs">
      <Group justify="space-between" gap="xs">
        <Text size="sm">{notice.text}</Text>
        {notice.action && <BrandLink href={`${brandHref}/${notice.path}`}>{notice.action}</BrandLink>}
      </Group>
    </Alert>
  );

  const swatchList = (name) => (
    <Stack gap={6} key={name}>
      <Text size="xs" tt="uppercase" fw={600} c="dimmed">
        {name === 'light' ? 'Light' : 'Dark'}
      </Text>
      {WIDGET_ROLES.map((role) => (
        <SwatchRow
          key={role.key}
          role={role}
          {...resolved[name]}
          tabIndex={0}
          title={role.where}
          {...roleHandlers(role.key)}
        />
      ))}
    </Stack>
  );

  const customRow = (role) => {
    const { palette, from } = resolved[editSide];
    if (editSide === 'dark' && role.key === 'primary') {
      return (
        <SwatchRow
          key={role.key}
          role={role}
          palette={palette}
          from={from}
          note=". Changed on the Light tab."
          {...roleHandlers(role.key)}
        />
      );
    }
    const overridden = from[role.key] === 'override';
    const failing = contrastIssues(resolved[editSide]).filter((issue) => issue.role === role.key);
    const fix = overridden && failing.length ? readableFix(role.key, palette) : null;
    return (
      <Grid key={role.key} align="center" gutter="xs" {...roleHandlers(role.key)}>
        <Grid.Col span={{ base: 12, xs: 4 }}>
          <Text size="sm" fw={500}>
            {role.label}
          </Text>
          <Text size="xs" c="dimmed">
            {role.where}
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 4 }}>
          <HexInput
            aria-label={`${role.label} (${editSide})`}
            value={palette[role.key]}
            onCommit={(value) => setOverride(editSide, role.key, value)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 4 }}>
          <Group gap={6}>
            {failing.length > 0 && (
              <Tooltip label={`${failing.map((issue) => issue.label).join(', ')} is hard to read`} withArrow>
                <Badge color="orange" variant="light" size="sm">
                  ⚠ {Math.min(...failing.map((issue) => issue.ratio)).toFixed(1)}:1
                </Badge>
              </Tooltip>
            )}
            {fix && (
              <Button size="compact-xs" variant="light" onClick={() => setOverride(editSide, role.key, fix)}>
                Fix
              </Button>
            )}
            {overridden ? (
              <>
                <Badge variant="light" size="sm">
                  custom
                </Badge>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  aria-label={`Reset ${role.label}`}
                  onClick={() => setOverride(editSide, role.key, null)}
                >
                  Reset
                </Button>
              </>
            ) : (
              <Badge variant="outline" color="gray" size="sm">
                {ready ? 'from brand' : 'default'}
              </Badge>
            )}
          </Group>
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <>
      <Divider my="md" label="Colors" labelPosition="left" />
      <Grid mb="md" gutter="lg">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="sm">
            <SettingRow label="Colors" help={<HelpPopover {...HELP.source} onUse={pickSource} />}>
              <SegmentedControl
                fullWidth
                size="xs"
                aria-label="Color source"
                value={settings.source}
                onChange={pickSource}
                data={[
                  { value: 'default', label: 'Releva default' },
                  {
                    value: 'brand',
                    disabled: !ready && settings.source !== 'brand',
                    label: (
                      <Group gap={4} justify="center" wrap="nowrap">
                        Brand Identity
                        {brandState === 'running' && <Loader size={10} />}
                      </Group>
                    ),
                  },
                  { value: 'custom', label: 'Custom' },
                ]}
              />
            </SettingRow>
            <SettingRow label="Appearance" help={<HelpPopover {...HELP.mode} onUse={pickMode} />}>
              <SegmentedControl
                fullWidth
                size="xs"
                aria-label="Appearance"
                value={settings.mode}
                onChange={pickMode}
                data={MODE_OPTIONS}
              />
            </SettingRow>

            {askKeep && (
              <Alert color="blue" variant="light" p="xs">
                <Text size="sm">You have your own main color ({legacyColor.toUpperCase()}). Keep it?</Text>
                <Group gap="xs" mt="xs">
                  <Button
                    size="compact-sm"
                    onClick={() => {
                      setAskKeep(null);
                      setColors({ source: 'custom', light: { ...settings.light, primary: legacyColor.toLowerCase() } });
                    }}
                  >
                    Keep {legacyColor.toUpperCase()}
                  </Button>
                  <Button
                    size="compact-sm"
                    variant="default"
                    onClick={() => {
                      setColors({ source: askKeep });
                      setAskKeep(null);
                    }}
                  >
                    {ready ? "Use the brand's main color" : 'Use the default'}
                  </Button>
                </Group>
              </Alert>
            )}

            {settings.source === 'default' && (
              <>
                <SettingRow label="Own main color" htmlFor="customThemeEnabled">
                  <Switch
                    id="customThemeEnabled"
                    checked={chatBot.customThemeColor !== null}
                    onChange={(e) =>
                      handleChange({ target: { name: 'customThemeEnabled', value: e.currentTarget.checked } })
                    }
                  />
                </SettingRow>
                {chatBot.customThemeColor !== null && (
                  <SettingRow label="Main color">
                    <Box {...roleHandlers('primary')}>
                      <ColorInput
                        format="hex"
                        size="xs"
                        aria-label="Main color"
                        value={chatBot.customThemeColor || ''}
                        onChange={(value) => handleChange({ target: { name: 'customThemeColor', value } })}
                      />
                    </Box>
                  </SettingRow>
                )}
                {ready ? (
                  <Alert color="gray" variant="light" p="xs">
                    <Group justify="space-between" gap="xs">
                      <Text size="sm">Your brand colors are ready. Use them to match the chat to your site.</Text>
                      <Button size="compact-sm" onClick={() => pickSource('brand')}>
                        Use Brand Identity
                      </Button>
                    </Group>
                  </Alert>
                ) : (
                  brandNotice
                )}
              </>
            )}

            {settings.source === 'brand' &&
              (ready ? (
                <>
                  <Group justify="space-between" gap="xs">
                    <Text size="sm" c="dimmed">
                      Linked to your Brand Identity. Changes there show up here.
                    </Text>
                    <BrandLink href={brandIdentityHref}>Edit in Brand Identity</BrandLink>
                  </Group>
                  <Grid>
                    {sides.map((name) => (
                      <Grid.Col key={name} span={{ base: 12, sm: 6 }}>
                        {swatchList(name)}
                      </Grid.Col>
                    ))}
                  </Grid>
                  <Group justify="space-between" gap="xs">
                    <ContrastLine issues={issues} />
                    <Button size="compact-sm" variant="subtle" onClick={() => setColors({ source: 'custom' })}>
                      Customize from these →
                    </Button>
                  </Group>
                </>
              ) : brandContext === undefined ? (
                <Group gap="xs">
                  <Loader size={12} />
                  <Text size="sm" c="dimmed">
                    Reading your Brand Identity colors…
                  </Text>
                </Group>
              ) : (
                <Alert color="yellow" variant="light" p="xs">
                  <Text size="sm">
                    Brand Identity is not confirmed, so the chat shows Releva&apos;s default colors.
                  </Text>
                  <Group gap="xs" mt="xs">
                    <BrandLink href={brandIdentityHref}>Open Brand Identity</BrandLink>
                    <Button size="compact-sm" variant="default" onClick={() => setColors({ source: 'default' })}>
                      Switch to Releva default
                    </Button>
                  </Group>
                </Alert>
              ))}

            {settings.source === 'custom' && (
              <>
                {!ready && brandNotice}
                <Group justify="space-between" gap="xs">
                  <Text size="sm" c="dimmed">
                    {ready
                      ? "Starts from your brand. Colors you haven't changed keep following it."
                      : 'Starts from Releva defaults until your brand is confirmed.'}
                  </Text>
                  {overrideCount > 0 && (
                    <Button size="compact-sm" variant="subtle" onClick={() => setColors({ light: {}, dark: {} })}>
                      Reset {overrideCount} to {ready ? 'brand' : 'default'}
                    </Button>
                  )}
                </Group>
                {ready && <BrandLink href={brandIdentityHref}>Edit in Brand Identity</BrandLink>}
                {settings.mode !== 'light' && (
                  <SegmentedControl
                    size="xs"
                    aria-label="Color set"
                    value={editSide}
                    onChange={setSide}
                    data={SIDE_OPTIONS}
                  />
                )}
                <Stack gap="xs">{WIDGET_ROLES.map(customRow)}</Stack>
                <ContrastLine issues={issues} />
              </>
            )}

            {settings.source !== 'custom' && overrideCount > 0 && (
              <Text size="xs" c="dimmed">
                Your custom colors are kept. Switch back to Custom to use them.
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Box style={{ position: 'sticky', top: 16 }}>
            <WidgetColorPreview
              resolved={resolved}
              side={side}
              onSideChange={setSide}
              mode={settings.mode}
              activeRole={activeRole}
              title={chatBot.title?.default}
            />
          </Box>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default WidgetColorsSection;
